import { Check, Copy } from 'lucide-react'
import { useId, useState } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

export function SectionHeading({
  title,
  description,
  action,
  size = 'md',
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  size?: 'md' | 'lg' | 'xl'
}) {
  const sizes = { md: 'text-2xl', lg: 'text-4xl', xl: 'text-5xl sm:text-6xl' }
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-2xl">
        <h2 className={`${sizes[size]} font-bold text-ink`}>{title}</h2>
        {description && <p className="mt-2 text-[16px] leading-relaxed text-ink-2">{description}</p>}
      </div>
      {action}
    </div>
  )
}

const BADGE_TONES = {
  neutral: 'bg-subtle text-ink-2',
  accent: 'bg-accent-soft text-accent',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
} as const
export type BadgeTone = keyof typeof BADGE_TONES

export function Badge({
  tone = 'neutral',
  children,
  dot,
  title,
}: {
  tone?: BadgeTone
  children: ReactNode
  dot?: boolean
  title?: string
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-[3px] px-1.5 py-0.5 font-mono text-[11px] tracking-wide uppercase ${BADGE_TONES[tone]}`}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  )
}

export function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="label-mono">{label}</p>
      <p className="font-display text-5xl font-bold tabular-nums text-ink">{value}</p>
      {hint && <p className="text-[13px] text-ink-3">{hint}</p>}
    </div>
  )
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
}) {
  const id = useId()
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start justify-between gap-4 py-1">
      <span>
        <span className="block text-[15px] text-ink">{label}</span>
        {description && <span className="mt-0.5 block text-[13px] text-ink-3">{description}</span>}
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-10 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
          checked ? 'bg-ink' : 'bg-line-strong/60'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-bg shadow transition-transform ${
            checked ? 'translate-x-4' : ''
          }`}
        />
      </button>
    </label>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
}) {
  // Hints/errors sit outside the <label> so the control's accessible name is just the label.
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-ink-2">{label}</span>
        {children}
      </label>
      {error ? (
        <span className="text-[13px] text-danger" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="text-[13px] text-ink-3">{hint}</span>
      )}
    </div>
  )
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input ${props.className ?? ''}`} />
}

export function ErrorText({ children }: { children?: ReactNode }) {
  if (!children) return null
  return (
    <p role="alert" className="border-l-2 border-danger bg-danger-soft px-3 py-2 text-[14px] text-danger">
      {children}
    </p>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="grid-paper flex flex-col items-center gap-3 border border-dashed border-line px-6 py-14 text-center">
      {icon && <div className="text-ink-3">{icon}</div>}
      <p className="font-display text-2xl font-bold">{title}</p>
      {description && <p className="max-w-sm text-[14px] text-ink-2">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function CopyButton({
  value,
  label = 'Copy',
  className = 'btn-ghost btn-sm',
}: {
  value: string
  label?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className={className}
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : label}
    </button>
  )
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-[3px] bg-subtle ${className}`} />
}
